import React from "react";
import { Link } from "react-router-dom";
import blogPosts from "../data/barbequeen-blog-posts.json";

/* ─── Helpers ─────────────────────────────────────────────────── */
const getCategory = (post) => {
  const s = (post.slug || "").toLowerCase();
  const t = (post.title || "").toLowerCase();
  if (s.includes("briyani") || s.includes("biryani") || t.includes("biryani")) return "Biryani";
  if (s.includes("grill") || s.includes("bbq") || t.includes("grill") || t.includes("tandoori")) return "BBQ & Grill";
  if (s.includes("catering") || s.includes("event") || t.includes("catering")) return "Events";
  if (s.includes("restaurant") || s.includes("outlet") || t.includes("restaurant")) return "Dining";
  if (s.includes("chicken") || t.includes("chicken")) return "Chicken";
  return "Food & More";
};

const CATEGORY_COLORS = {
  Biryani: { bg: "#fdf0f2", text: "#9b1c2e", border: "#f87171" },
  "BBQ & Grill": { bg: "#fffbeb", text: "#b45309", border: "#f59e0b" },
  Events: { bg: "#eff6ff", text: "#0369a1", border: "#38bdf8" },
  Dining: { bg: "#ecfdf5", text: "#065f46", border: "#34d399" },
  Chicken: { bg: "#f5f3ff", text: "#7c3aed", border: "#a78bfa" },
  "Food & More": { bg: "#f3f4f6", text: "#374151", border: "#9ca3af" },
};

const getFeaturedImage = (post) => {
  if (post.featuredImage) return post.featuredImage;
  return "https://images.unsplash.com/photo-1544025162-d76694265947?q=80&w=800&auto=format&fit=crop";
};

const getReadTime = (post) => {
  const text = post.content || post.excerpt || "";
  return Math.max(2, Math.ceil(text.split(/\s+/).length / 180));
};

/* Take top 3 featured posts for homepage section */
const featuredPosts = blogPosts.slice(0, 3);

/* ─── Single Blog Card ─────────────────────────────────────────── */
const BlogCard = ({ post }) => {
  const category = getCategory(post);
  const meta = CATEGORY_COLORS[category] || CATEGORY_COLORS["Food & More"];
  const image = getFeaturedImage(post);
  const readTime = getReadTime(post);

  return (
    <article className="group flex flex-col bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-2xl hover:-translate-y-1.5 transition-all duration-300">
      {/* Image Container */}
      <div className="relative overflow-hidden h-[230px] flex-shrink-0">
        <img
          src={image}
          alt={post.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          loading="lazy"
          onError={(e) => {
            e.target.src = "https://images.unsplash.com/photo-1504674900247-0877df9cc836?q=80&w=800&auto=format&fit=crop";
          }}
        />
        {/* Subtle Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300" />

        {/* Category Tag */}
        <div
          className="absolute top-4 left-4 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider shadow-md backdrop-blur-sm"
          style={{ backgroundColor: meta.bg, color: meta.text, border: `1px solid ${meta.border}` }}
        >
          {category}
        </div>

        {/* Read Time Tag */}
        <div className="absolute bottom-3 right-3 text-[11px] font-medium text-white/90 bg-black/50 px-2.5 py-1 rounded-full backdrop-blur-sm">
          {readTime} min read
        </div>
      </div>

      {/* Content Container */}
      <div className="flex flex-col flex-1 p-6">
        {/* Meta Bar */}
        <div className="flex items-center gap-2 text-[12px] text-gray-400 mb-3 font-sans">
          <svg className="w-4 h-4 text-[#9b1c2e]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span className="font-semibold text-gray-500">Barbequeen Official</span>
        </div>

        {/* Title */}
        <h3 className="text-[18px] font-bold text-gray-900 leading-snug mb-3 group-hover:text-[#9b1c2e] transition-colors duration-200 line-clamp-2">
          {post.title}
        </h3>

        {/* Excerpt */}
        <p className="font-sans text-[13px] text-gray-600 leading-relaxed flex-1 line-clamp-3 mb-5">
          {post.excerpt || post.metaDescription || "Discover stories, recipes, and dining guides straight from Barbequeen kitchen."}
        </p>

        {/* Read More Link */}
        <div className="pt-4 border-t border-gray-100 flex items-center justify-between">
          <Link
            to={`/blog/${post.slug}`}
            className="font-sans inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wider text-[#9b1c2e] group-hover:gap-3 transition-all duration-200"
            aria-label={`Read more about ${post.title}`}
          >
            <span>Read Full Article</span>
            <svg className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </div>
    </article>
  );
};

/* ─── Main Blog Section Component ─────────────────────────────── */
const BlogSection = () => (
  <section id="blog" className="py-20 lg:py-28 bg-[#fbf9f6] border-t border-gray-100">
    <div className="max-w-[1280px] mx-auto px-4 lg:px-8">

      {/* Section Header */}
      <div className="text-center mb-14">
        <p className="font-sans text-[11px] font-bold tracking-[0.3em] text-[#9b1c2e] uppercase mb-3 flex items-center justify-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#9b1c2e]" />
          Blog
          <span className="w-2 h-2 rounded-full bg-[#9b1c2e]" />
        </p>
        <h2 className="text-[32px] sm:text-[42px] lg:text-[48px] font-bold text-gray-900 leading-tight uppercase tracking-wide">
          Explore Our
          <br />
          Latest Articles
        </h2>
        <p className="mt-4 text-gray-600 text-sm sm:text-base max-w-xl mx-auto font-sans leading-relaxed">
          Stay updated with culinary insights, secret recipes, BBQ guides, and news from Barbequeen.
        </p>
      </div>

      {/* Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {featuredPosts.map((post, idx) => (
          <BlogCard key={post.id || post.slug || idx} post={post} />
        ))}
      </div>

      {/* View All Button */}
      <div className="text-center mt-14">
        <Link
          to="/blog"
          className="font-sans inline-flex items-center gap-2 px-9 py-3.5 bg-[#9b1c2e] hover:bg-[#7d1624] text-white text-[13px] font-bold uppercase tracking-widest transition-all duration-300 rounded-full shadow-md hover:shadow-xl"
        >
          <span>View All Articles</span>
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
          </svg>
        </Link>
      </div>

    </div>
  </section>
);

export default BlogSection;
